import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
} from '@mui/material';

import { validLanguages } from '@remote-voice/utilities';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CancelButton from '@/components/atoms/CancelButton';

export type ProjectionModeDialogProps = {
  open: boolean;
  onClose: (result?: {
    language: string[];
    firstItemIsAutoPlay: boolean;
  }) => void;
  defaultLanguages: string[] | undefined;
};

const ProjectionModeDialog = (props: ProjectionModeDialogProps) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tChat } = useTranslation('chat');
  const [selectedLanguages, setSelectedLanguages] = useState<
    string[] | undefined
  >(props.defaultLanguages);

  const [firstItemIsAutoPlay, setFirstItemIsAutoPlay] = useState(false);

  useEffect(() => {
    setSelectedLanguages(props.defaultLanguages);
  }, [props.defaultLanguages]);

  const handleLanguageToggle = (language: string) => {
    setSelectedLanguages((prev) => {
      const current = prev ?? [];
      if (current.includes(language)) {
        return current.filter((l) => l !== language);
      }
      if (current.length >= 2) {
        return current;
      }
      return [...current, language];
    });
  };

  return (
    <Dialog open={props.open}>
      <DialogTitle sx={{ whiteSpace: 'pre-wrap' }}>
        <Stack alignItems="center">
          {tChat('projectionMode.dialogTitle')}
          <FormControlLabel
            control={
              <Checkbox
                value={firstItemIsAutoPlay}
                onChange={(ev) => setFirstItemIsAutoPlay(ev.target.checked)}
              />
            }
            label="一つ目の言語を自動再生する"
          />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <FormGroup>
          {validLanguages.map((language) => (
            <FormControlLabel
              key={language}
              control={
                <Checkbox
                  checked={selectedLanguages?.includes(language) ?? false}
                  onChange={() => handleLanguageToggle(language)}
                  disabled={
                    !(selectedLanguages ?? []).includes(language) &&
                    (selectedLanguages ?? []).length >= 2
                  }
                />
              }
              label={
                <Stack direction="row" spacing={1}>
                  <Box>{tCommon(`languages.${language}` as any)}</Box>

                  {selectedLanguages?.[0] === language && (
                    <LooksOneIcon color="primary" fontSize="small" />
                  )}
                  {selectedLanguages?.[1] === language && (
                    <LooksTwoIcon color="primary" fontSize="small" />
                  )}
                  {selectedLanguages?.[0] === language &&
                    firstItemIsAutoPlay && (
                      <VolumeUpIcon color="primary" fontSize="small" />
                    )}
                </Stack>
              }
            />
          ))}
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="center"
          gap={1}
          width={1}
        >
          <CancelButton onClick={() => props.onClose()} />
          <Button
            onClick={() =>
              props.onClose({
                language: selectedLanguages ?? [],
                firstItemIsAutoPlay,
              })
            }
            disabled={(selectedLanguages?.length ?? 0) == 0}
          >
            {tCommon('command.ok')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectionModeDialog;
